import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { startAuthentication } from '@simplewebauthn/browser';
import useSound from 'use-sound';
import sounds from '../sounds.mp3';
import { useAuth } from '../contexts/AuthContext';
import { SCREEN_NAME_PATTERN, getScreenNameHint } from '../screenNameValidation';

const soundMap = {
  connect: [20800, 1000],
  msgRcv: [8600, 1000],
  error: [23700, 1000],
};

export default function Login() {
  const [screenName, setScreenName] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [magicMode, setMagicMode] = useState(false);
  const [magicEmail, setMagicEmail] = useState('');
  const [magicSent, setMagicSent] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const [play] = useSound(sounds, { interrupt: true, sprite: soundMap });

  const handlePasskeyLogin = async (e) => {
    e.preventDefault();
    setError('');
    const screenNameHint = getScreenNameHint(screenName, { required: false });
    if (screenNameHint) {
      setError(screenNameHint);
      play({ id: 'error' });
      return;
    }
    setSubmitting(true);
    try {
      // Step 1: Get authentication options
      const optRes = await fetch('/api/passkey/login-options', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ screen_name: screenName || undefined }),
      });
      const optData = await optRes.json();
      if (!optRes.ok) {
        setError(optData.error);
        play({ id: 'error' });
        return;
      }

      // Step 2: Browser WebAuthn ceremony
      const assertionResponse = await startAuthentication({ optionsJSON: optData.options });

      // Step 3: Verify with server
      const verifyRes = await fetch('/api/passkey/login-verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          challengeToken: optData.challengeToken,
          assertionResponse,
        }),
      });
      const verifyData = await verifyRes.json();
      if (!verifyRes.ok) {
        setError(verifyData.error);
        play({ id: 'error' });
        return;
      }

      play({ id: 'connect' });
      login(verifyData.token, verifyData.user);
      navigate('/');
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setError('Passkey authentication was cancelled.');
      } else {
        setError('Login failed. Try again.');
      }
      play({ id: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await fetch('/api/magic-link/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: magicEmail }),
      });
      if (res.ok) {
        setMagicSent(true);
        play({ id: 'msgRcv' });
      }
    } catch {
      setError('Failed to send magic link.');
      play({ id: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const screenNameHint = getScreenNameHint(screenName, { required: false });

  return (
    <div className="buddy-info window">
      <div className="title-bar">
        <span className="title-bar-text">
          <span>Sign On</span>
        </span>
        <div className="title-bar-controls">
          <button aria-label="Close" onClick={() => navigate('/')}></button>
        </div>
      </div>
      <div className="window-body">
        {!magicMode ? (
          <form onSubmit={handlePasskeyLogin}>
            <div className="field-row-stacked" style={{ width: '100%' }}>
              <label htmlFor="login-sn">Screen Name (optional):</label>
              <input
                id="login-sn"
                type="text"
                value={screenName}
                onChange={(e) => setScreenName(e.target.value)}
                placeholder="Leave blank to use any passkey"
                maxLength={16}
                pattern={SCREEN_NAME_PATTERN}
                title="Leave blank, or enter 3-16 characters that start with a letter and use letters, numbers, and underscores only"
              />
              {screenNameHint && <small style={{ color: '#c00' }}>{screenNameHint}</small>}
            </div>
            <p style={{ fontSize: 11, color: '#666', margin: '4px 0' }}>
              🔑 You'll be prompted to authenticate with your passkey (fingerprint, face, or security key).
            </p>
            {error && <p style={{ color: '#c00', fontSize: 12 }}>{error}</p>}
            <div className="field-row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
              <button type="submit" disabled={submitting}>
                {submitting ? 'Signing In...' : 'Sign On with Passkey'}
              </button>
            </div>
            <hr />
            <p style={{ fontSize: 11 }}>
              <button type="button" onClick={() => setMagicMode(true)} style={{ fontSize: 11 }}>
                Sign in with magic link instead
              </button>
              {' | '}
              <button type="button" onClick={() => navigate('/register')} style={{ fontSize: 11 }}>
                Get a Screen Name
              </button>
            </p>
          </form>
        ) : magicSent ? (
          <div>
            <p>✉️ Check your email! A sign-in link has been sent.</p>
            <p style={{ fontSize: 11 }}>The link expires in 15 minutes.</p>
            <button onClick={() => navigate('/')}>OK</button>
          </div>
        ) : (
          <form onSubmit={handleMagicLink}>
            <p>Enter the email address associated with your screen name:</p>
            <div className="field-row-stacked" style={{ width: '100%' }}>
              <label htmlFor="magic-email">Email:</label>
              <input
                id="magic-email"
                type="email"
                value={magicEmail}
                onChange={(e) => setMagicEmail(e.target.value)}
                required
              />
            </div>
            {error && <p style={{ color: '#c00', fontSize: 12 }}>{error}</p>}
            <div className="field-row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
              <button type="submit" disabled={submitting}>Send Magic Link</button>
              <button type="button" onClick={() => setMagicMode(false)} style={{ marginLeft: 4 }}>Back</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
